import { useCallback } from "react";
import type { KeyboardEvent, RefObject } from "react";

import type { ListNavigationKey, ListVirtualizer } from "./List.types";
import { LIST_ITEM_SELECTOR, LIST_NAVIGATION_KEYS } from "./constants";

const ENABLED_ITEM_SELECTOR = `${LIST_ITEM_SELECTOR}:not([data-disabled])`;

const FOCUS_RETRY_MS = 16;

const FOCUS_MAX_ATTEMPTS = 12;

function isNavigationKey(key: string): key is ListNavigationKey {
  return (LIST_NAVIGATION_KEYS as readonly string[]).includes(key);
}

function nextIndex(key: ListNavigationKey, current: number, count: number) {
  switch (key) {
    case "Home":
      return 0;
    case "End":
      return count - 1;
    case "ArrowDown":
      return 0 > current ? 0 : Math.min(current + 1, count - 1);
    case "ArrowUp":
      return 0 > current ? count - 1 : Math.max(current - 1, 0);
  }
}

function activeElementOf(viewport: HTMLElement): Element | null {
  const root = viewport.getRootNode() as Document | ShadowRoot;

  return root.activeElement;
}

function activeIndexOf(viewport: HTMLElement): number {
  const active = activeElementOf(viewport);

  if (!(active instanceof HTMLElement) || !viewport.contains(active)) {
    return -1;
  }

  const item = active.closest<HTMLElement>(LIST_ITEM_SELECTOR);
  const index = Number(item?.dataset.index);

  return Number.isInteger(index) ? index : -1;
}

function focusVirtualIndex(
  virtualizer: ListVirtualizer,
  viewport: HTMLElement,
  index: number,
  step: number,
  attempt = 0,
) {
  if (0 > index || index >= virtualizer.options.count) {
    return;
  }

  if (0 === attempt) {
    virtualizer.scrollToIndex(index, { align: "auto" });
  }

  const node = viewport.querySelector<HTMLElement>(`[data-index="${index}"]`);

  if (node) {
    if (node.matches(ENABLED_ITEM_SELECTOR)) {
      node.focus();
    } else {
      focusVirtualIndex(virtualizer, viewport, index + step, step);
    }

    return;
  }

  if (attempt >= FOCUS_MAX_ATTEMPTS) {
    return;
  }

  setTimeout(
    () => focusVirtualIndex(virtualizer, viewport, index, step, attempt + 1),
    FOCUS_RETRY_MS,
  );
}

export function useListNavigation({
  viewportRef,
  virtualizerRef,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  virtualizerRef: RefObject<ListVirtualizer | null>;
}) {
  return useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const viewport = viewportRef.current;

      if (!viewport || !isNavigationKey(event.key)) {
        return;
      }

      const virtualizer = virtualizerRef.current;

      if (virtualizer) {
        const count = virtualizer.options.count;

        if (0 === count) {
          return;
        }

        event.preventDefault();

        const target = nextIndex(event.key, activeIndexOf(viewport), count);
        const step = "ArrowUp" === event.key || "End" === event.key ? -1 : 1;

        focusVirtualIndex(virtualizer, viewport, target, step);

        return;
      }

      const items = [
        ...viewport.querySelectorAll<HTMLElement>(ENABLED_ITEM_SELECTOR),
      ];

      if (0 === items.length) {
        return;
      }

      event.preventDefault();

      const current = items.indexOf(activeElementOf(viewport) as HTMLElement);

      items[nextIndex(event.key, current, items.length)]?.focus();
    },
    [viewportRef, virtualizerRef],
  );
}
